import { useState } from 'react';
import { PlatformIcon } from '@/components/primitives';
import { formatUsd, formatCount, formatArpu } from '@/lib/format';
import styles from './BannerBlock.module.css';

/**
 * The cover of a deliverable — subject identity, not analysis.
 *
 * Lifted from the d360 title profile's banner: poster at the left, title and
 * two metadata rows beside it, artwork bleeding out to the right behind them.
 * Two deliberate departures from that reference:
 *
 * - **The backdrop is the poster, blurred and scaled.** d360 uses a separate
 *   still; we don't have one, and inventing an asset pipeline for a decorative
 *   layer isn't worth it. Reusing the poster costs no new request — the browser
 *   serves it from cache — and reads as intentional rather than missing.
 * - **No synopsis and no action buttons.** We hold no synopsis text, and a row
 *   that renders empty is worse than a row that doesn't exist. Trailer and
 *   avails buttons belong to d360's product, not to a workflow deliverable.
 *
 * Compact by design (~180px). d360 can spend 380px on a hero because the page
 * *is* the profile; here the banner opens a document of blocks and has to earn
 * its space against the analysis below it.
 */

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';

/** Both banner sources return one object rather than a row list. */
export interface BannerSubject {
  // Title-shaped
  parrot_id?: string;
  display_name?: string;
  main_genre?: string;
  sub_genre?: string;
  release_year?: number;
  content_type?: string;
  original_country?: string;
  original_language?: string;
  total_episodes?: number;
  tv_series_type?: string;
  imdb_rating?: number;
  demand_label?: string;
  // Platform-shaped
  platform?: string;
  total_subscribers?: number;
  total_revenue?: number;
  avg_arpu?: number;
  region_count?: number;
}

/** ISO-3166 alpha-2 → regional-indicator pair. Avoids shipping flag assets. */
function flagOf(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function Backdrop({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className={styles.backdrop}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function Poster({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className={styles.posterFallback} aria-hidden="true" />;
  return (
    <img
      src={src}
      alt={alt}
      className={styles.poster}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/** One `·`-separated metadata row, with empty values dropped rather than shown. */
function MetaRow({ items }: { items: (string | null)[] }) {
  const shown = items.filter((i): i is string => Boolean(i));
  if (shown.length === 0) return null;
  return (
    <p className={styles.meta}>
      {shown.map((item, i) => (
        <span key={item + i}>
          {i > 0 && <span className={styles.sep} aria-hidden="true">·</span>}
          {item}
        </span>
      ))}
    </p>
  );
}

export function BannerBlock({ subject }: { subject: BannerSubject }) {
  const isPlatform = Boolean(subject.platform) && !subject.display_name;

  if (isPlatform) {
    const name = subject.platform!;
    return (
      <div className={styles.base} data-kind="platform">
        <div className={styles.body}>
          <span className={styles.logo}>
            <PlatformIcon name={name} size={64} />
          </span>
          <div className={styles.text}>
            <h2 className={styles.title}>{name}</h2>
            <MetaRow
              items={[
                subject.total_subscribers
                  ? `${formatCount(subject.total_subscribers)} subscribers`
                  : null,
                subject.avg_arpu ? `${formatArpu(subject.avg_arpu)} ARPU` : null,
                subject.total_revenue ? `${formatUsd(subject.total_revenue)} revenue` : null,
                subject.region_count ? `${subject.region_count} regions` : null,
              ]}
            />
          </div>
        </div>
      </div>
    );
  }

  const id = subject.parrot_id ?? '';
  const poster = id ? `${POSTER_CDN}/${id}.jpg?width=300` : '';
  const name = subject.display_name ?? 'Untitled';

  return (
    <div className={styles.base} data-kind="title">
      {/* Same URL as the poster, so this is a cache hit rather than a second
          download. Blur and scale happen in CSS. */}
      {poster && <Backdrop src={poster} />}
      <div className={styles.scrim} />

      <div className={styles.body}>
        {poster && <Poster src={poster} alt="" />}

        <div className={styles.text}>
          <h2 className={styles.title}>{name}</h2>

          <MetaRow
            items={[
              subject.main_genre ? titleCase(subject.main_genre) : null,
              subject.sub_genre ? titleCase(subject.sub_genre) : null,
              subject.release_year ? String(subject.release_year) : null,
              subject.content_type ? titleCase(subject.content_type) : null,
            ]}
          />

          <MetaRow
            items={[
              subject.original_country
                ? `${flagOf(subject.original_country)} ${subject.original_country}`.trim()
                : null,
              subject.original_language?.toUpperCase() ?? null,
              subject.total_episodes ? `${subject.total_episodes} episodes` : null,
              subject.tv_series_type ? titleCase(subject.tv_series_type) : null,
              subject.imdb_rating ? `IMDb ${subject.imdb_rating.toFixed(1)}` : null,
            ]}
          />

          {subject.demand_label && (
            <span className={styles.badge} data-level={subject.demand_label.toLowerCase()}>
              {subject.demand_label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
